/**
 * Create a cache function that caches the result of the function
 *
 * @param fn {Function}
 * @returns {Function}
 */
export function cached<R>(fn: (str: string) => R): (str: string) => R {
  const cache: Record<string, R> = Object.create(null)

  return function cachedFn(str) {
    if (!(str in cache)) {
      cache[str] = fn(str)
    }

    return cache[str]
  }
}

/**
 * Create a function that will call the function only once
 *
 * @param fn {Function}
 * @returns {Function}
 */
export function once<T extends (...args: any[]) => any>(fn: T): T {
  let called = false
  let result: any

  return function onceFn(this: any, ...args: any[]) {
    if (!called) {
      called = true
      result = fn.apply(this, args)
    }

    return result
  } as T
}

/**
 * Schedule a function to be called on the next tick
 *
 * @param fn {Function} - The function to be called on the next tick
 * @returns {Promise<void>} - A promise that resolves when the function is called
 */
export function nextTick(fn?: () => void): Promise<void> {
  return Promise.resolve().then(() => {
    if (typeof fn === 'function') {
      fn()
    }
  })
}
